import CheckIcon from '@mui/icons-material/Check';
import EmailIcon from '@mui/icons-material/Email';
import FlagIcon from '@mui/icons-material/Flag';
import ImageIcon from '@mui/icons-material/Image';
import MessageIcon from '@mui/icons-material/Message';
import MicIcon from '@mui/icons-material/Mic';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StarIcon from '@mui/icons-material/Star';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import { Alert, Divider, IconButton, Paper, Stack, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import DOMPurify from 'dompurify';
import { makeStyles } from 'tss-react/mui';
import {
    ActionPlanStatus,
    ActionTypes,
    DayType,
    PlanPriorities,
    PlanTypes,
} from '../../../sic-lib/enums/EngageActionPlansEnums';
import { EngageActionPlan } from '../../../sic-lib/types/EngageActionPlan';
import { UserAvatar } from '../../Settings/Users/UserAvatar';
import DisplayDateTime from '../../Shared/DisplayDateTime/DisplayDateTime';

interface Props {
    timelineData: EngageActionPlan[];
    mode?: string;
}

export default function AutomationDialogTimelineItem({ timelineData, mode = '' }: Props) {
    const { classes, cx } = useStyles();

    return (
        <Stack direction="column" justifyContent="flex-start" display="flex" flexGrow={1} gap={2}>
            {timelineData?.map((action, index) => (
                <Stack key={action.id} gap={4}>
                    <Stack direction="row" alignItems="center" gap={2}>
                        <IconButton
                            style={{
                                backgroundColor: getBackgroundColor(action.status, action.planType),
                                color: 'white',
                            }}
                        >
                            {getIcon(action.status, action.actionType, action.planType)}
                        </IconButton>
                        <Paper elevation={2} sx={{ display: 'flex', width: '100%' }}>
                            <Stack
                                direction="column"
                                justifyContent="flex-start"
                                width="100%"
                                p={2}
                                gap={1}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <Stack direction="row">
                                        {action.priority !== PlanPriorities.Normal &&
                                            action.actionType === ActionTypes.Task && (
                                                <FlagIcon color="error" />
                                            )}
                                        <Typography variant="subtitle2">
                                            {action.description}
                                        </Typography>
                                    </Stack>
                                    <Stack display="flex" justifyContent="flex-end">
                                        {action.breadcrumbs && action.breadcrumbs.length > 0 ? (
                                            <StarIcon />
                                        ) : undefined}
                                    </Stack>
                                </Stack>
                                {action.endDate ? (
                                    <DisplayDateTime dateText={action.endDate} variant="caption" />
                                ) : action.status === ActionPlanStatus.NotScheduled ? (
                                    <Typography variant="caption">
                                        'waiting to be scheduled'
                                    </Typography>
                                ) : null}
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="flex-start"
                                >
                                    {action.overdue && action.dateType === DayType.Deadline && (
                                        <Typography variant="body2" className={classes.failed}>
                                            Overdue
                                        </Typography>
                                    )}
                                    {action.status === ActionPlanStatus.Deleted && (
                                        <Typography variant="body2">
                                            Deleted{' '}
                                            {<DisplayDateTime dateText={action.deleteDate} />} By{' '}
                                            {action.deletedBy}
                                        </Typography>
                                    )}
                                    {action.status === ActionPlanStatus.Completed && (
                                        <Typography variant="body2" className={classes.completed}>
                                            Completed{' '}
                                            {
                                                <DisplayDateTime
                                                    dateText={action.completeDate}
                                                    className={classes.completed}
                                                />
                                            }{' '}
                                            By {action.completeBy}
                                        </Typography>
                                    )}
                                    {action.status === ActionPlanStatus.NotScheduled && (
                                        <Typography variant="body2">{`This ${
                                            action.actionType
                                        } will be scheduled [ ${
                                            action.relativeTime &&
                                            getTime(
                                                action.daySpan,
                                                action.hourSpan,
                                                action.minuteSpan,
                                                action.relativeTime,
                                                action.prettyTime,
                                            )
                                        } ] after its parent action is Completed.`}</Typography>
                                    )}
                                    {action.status === ActionPlanStatus.Running && (
                                        <Typography variant="body2">Sending...</Typography>
                                    )}
                                    {action.statusMessage && (
                                        <Alert severity="warning" children={action.statusMessage} />
                                    )}

                                    {mode !== 'Template' && mode !== 'Preview' && (
                                        <UserAvatar userName={action.assignedUser} scale={1.5} />
                                    )}
                                </Stack>
                                {(action.actionType === ActionTypes.SMS ||
                                    action.actionType === ActionTypes.Email ||
                                    action.actionType === ActionTypes.Voicemail ||
                                    action.actionType === ActionTypes.Picture ||
                                    action.actionType === ActionTypes.Video) && (
                                    <Stack gap={1}>
                                        {action.actionType === ActionTypes.Email && (
                                            <Stack gap={1}>
                                                <Divider />
                                                <Typography variant="body2">
                                                    Subject: {action.actionCustomSubject}
                                                </Typography>
                                                {/* <Divider />
                                                <Text variant="labelSmall">
                                                    {action.actionCustomBody}
                                                </Text> */}
                                            </Stack>
                                        )}
                                        {(action.actionType === ActionTypes.SMS ||
                                            action.actionType === ActionTypes.Email) && (
                                            <Stack gap={1}>
                                                <Divider />
                                                <Typography variant="body2">
                                                    <div
                                                        dangerouslySetInnerHTML={{
                                                            __html: DOMPurify.sanitize(
                                                                action.actionCustomSubject,
                                                            ),
                                                        }}
                                                    />
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Stack>
                                )}
                            </Stack>
                        </Paper>
                    </Stack>
                    <Stack className={classes.marginLeft}>
                        <AutomationDialogTimelineItem timelineData={action.actions} />
                    </Stack>
                </Stack>
            ))}
        </Stack>
    );
}

const useStyles = makeStyles()((theme: Theme) => ({
    active: {
        color: theme.palette.conversationTimelinePaper.light,
    },
    completed: {
        color: theme.palette.conversationTimelinePaper.light,
    },
    paused: {
        color: theme.palette.warning.light,
    },
    failed: {
        color: theme.palette.error.light,
    },
    marginLeft: {
        marginLeft: theme.spacing(4),
    },
}));

const getTime = (
    day: number | null,
    hour: number | null,
    minute: number | null,
    relativeTime: boolean,
    prettyTime: string | null,
) => {
    if (relativeTime) {
        if (day) {
            return `${day} days`;
        } else if (hour) {
            return `${hour} hours`;
        } else if (minute) {
            return `${minute} minutes`;
        } else {
            return `Immediately`;
        }
    } else {
        if (prettyTime) {
            return prettyTime;
        } else {
            return 'Immediately';
        }
    }
};

const getIcon = (status: ActionPlanStatus, aType: ActionTypes, planType: PlanTypes) => {
    if (aType === ActionTypes.Task && planType === 'LeadPlanHeader') {
        return <StarIcon />;
    }
    if (aType === ActionTypes.Task) {
        return <CheckIcon />;
    }
    if (aType === ActionTypes.Email) {
        return <EmailIcon />;
    }
    if (aType === ActionTypes.SMS) {
        return <MessageIcon />;
    }
    if (aType === ActionTypes.Voicemail) {
        return <MicIcon />;
    }
    if (aType === ActionTypes.Picture) {
        return <ImageIcon />;
    }
    if (aType === ActionTypes.Video) {
        return <VideoFileIcon />;
    }
    if (status === ActionPlanStatus.EventPaused && aType === ActionTypes.DisplayEvent) {
        return <PauseIcon />;
    }
    if (status === ActionPlanStatus.EventResumed && aType === ActionTypes.DisplayEvent) {
        return <PlayArrowIcon />;
    }
    return <StarIcon />;
};

const getBackgroundColor = (status: ActionPlanStatus, pType: PlanTypes) => {
    if (
        status === ActionPlanStatus.Deleted ||
        status === ActionPlanStatus.NoEngageUser ||
        status === ActionPlanStatus.FailedNoConsent ||
        status === ActionPlanStatus.Failed
    ) {
        return '#f44336';
    } else if (status === ActionPlanStatus.Completed) {
        return '#76c016';
    } else if (status === ActionPlanStatus.Paused) {
        return '#ffd300';
    } else if (
        (status === ActionPlanStatus.Active && pType === PlanTypes.LeadPlanHeader) ||
        status === ActionPlanStatus.Running
    ) {
        return '#0b6fba';
    }
    return '#bdbdbd';
};
